import type { JSX } from 'react';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { ServiceUrlObject } from '@common/interfaces/serviceUrlObject';
import type { IServiceStartingPoint } from '@common/interfaces/serviceWorkflow';
import { ServiceType } from '@common/enums';
import { compareByServiceTypeButtonSortOrder } from '@common/utils/serviceSortUtils';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { ProcessStartWizard } from '@frontend/components/elements/ProcessStartWizard/ProcessStartWizard';
import { EmptyEpisode } from '@frontend/components/pages/episodes/EmptyEpisode';
import { NewProcessApi } from '@frontend/components/pages/home/newProcess/api/newProcessApi';
import { getSelectedLanguage } from '@frontend/translate';
import { NewProcessButtonStartBar } from '../../home/newProcess/NewProcessButtonStartBar';

type ProcessStartWrapperProps = Readonly<{
    episodeCategory: EpisodeCategory;
    isEmptyEpisode: boolean;
    onStartingPointSelected: (startingPoint: IServiceStartingPoint) => void;
    patientId: string;
}>;

const newProcessApi = new NewProcessApi();

export function ProcessStartWrapper(props: ProcessStartWrapperProps): JSX.Element | null {
    const { episodeCategory, isEmptyEpisode, onStartingPointSelected, patientId } = props;
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
    const [selectedServiceType, setSelectedServiceType] = useState<ServiceType>();

    useEffect(() => {
        loadServices(episodeCategory).finally(() => setIsLoading(false));
    }, [episodeCategory]);

    if (isLoading) {
        return null;
    }

    if (isEmptyEpisode && !selectedServiceType) {
        return (
            <EmptyEpisode
                episodeCategory={episodeCategory}
                onClick={setSelectedServiceType}
                serviceTypes={serviceTypes}
            />
        );
    }

    return (
        <ProcessStartWizard
            episodeCategory={episodeCategory}
            patient={{ id: patientId }}
            serviceType={selectedServiceType}
        >
            <NewProcessButtonStartBar
                defaultSelectedEpisodeCategory={episodeCategory}
                defaultSelectedServiceType={selectedServiceType}
                defaultTriggerOnClick={{ serviceType: true }}
                hideEpisodeCategoryBar={true}
                label={getSelectedLanguage().home.startOne}
                onServiceIdSelected={onServiceIdSelected}
                onStartingPointSelected={onStartingPointSelected}
                patient={{ id: patientId }}
                showServiceTypeBar={true}
            />
        </ProcessStartWizard>
    );

    async function loadServices(episodeCategoryName: string): Promise<void> {
        const { services: newServices } = await newProcessApi.loadServiceTypes();
        const episodeCategories = await newProcessApi.loadEpisodeCategories();
        const currentCategory = episodeCategories.find(
            (episodeCategory) => episodeCategory.name === episodeCategoryName
        );
        if (!currentCategory) {
            return;
        }
        const episodeCategoryServices = newServices.filter(
            (service) => service.episodeCategoryId === currentCategory.id
        );
        const newServiceTypes = episodeCategoryServices.map((service) =>
            service.serviceType === ServiceType.inquiry ? ServiceType.examination : service.serviceType
        );
        const uniqueServiceTypes = [...new Set(newServiceTypes)];
        uniqueServiceTypes.sort(compareByServiceTypeButtonSortOrder);

        setServiceTypes(uniqueServiceTypes);
    }

    function onServiceIdSelected(args: ServiceUrlObject): void {
        const { hasStartingPoints, newProcessCustomIdentifier, serviceId, withProcedure } = args;
        if (serviceId && !hasStartingPoints) {
            const urlBuilder = new UrlBuilder('/episode');
            urlBuilder
                .add('patientId', patientId)
                .add('episodeCategory', episodeCategory)
                .add('serviceId', serviceId)
                .add('withProcedure', withProcedure);

            if (newProcessCustomIdentifier) {
                urlBuilder.add('newProcessCustomIdentifier', newProcessCustomIdentifier);
            }

            navigate(urlBuilder.build());
        }
    }
}
